import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { 
  MapPin, Navigation, Truck, RefreshCw, AlertCircle, 
  Phone, Package, ChevronDown, Users, Route
} from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Color palette for different drivers
const DRIVER_COLORS = [
  '#2563eb', '#dc2626', '#16a34a', '#9333ea', 
  '#ea580c', '#0891b2', '#be185d', '#4f46e5',
];

// Helper to format time ago
const formatTimeAgo = (date) => {
  if (!date) return 'Unknown';
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(date).toLocaleDateString();
};

// Calculate distance between two points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

// Create custom icons
const createDriverIcon = (color) => L.divIcon({
  html: `<div style="background: ${color}; width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
      <path d="M10 17h4V5H2v12h3m15 0h2v-4h-4V9h4l3 4v4h-2"></path>
      <circle cx="7" cy="17" r="2"></circle>
      <circle cx="17" cy="17" r="2"></circle>
    </svg>
  </div>`,
  className: 'driver-marker',
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20]
});

const createDestinationIcon = (color) => L.divIcon({
  html: `<div style="background: ${color}; width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.2); opacity: 0.85;">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
      <circle cx="12" cy="10" r="3"></circle>
    </svg>
  </div>`,
  className: 'destination-marker',
  iconSize: [28, 28],
  iconAnchor: [14, 28],
  popupAnchor: [0, -28]
});

const warehouseIcon = L.divIcon({
  html: `<div style="background: #374151; width: 44px; height: 44px; border-radius: 8px; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 10px rgba(0,0,0,0.3);">
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
      <path d="M3 21h18"></path>
      <path d="M5 21V7l7-4 7 4v14"></path>
      <path d="M9 21v-4h6v4"></path>
      <path d="M9 9h6"></path>
      <path d="M9 13h6"></path>
    </svg>
  </div>`,
  className: 'warehouse-marker',
  iconSize: [44, 44],
  iconAnchor: [22, 22],
  popupAnchor: [0, -22]
});

const AdminDriverTrackingMap = () => {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDestinations, setShowDestinations] = useState(true);
  const [showRoutes, setShowRoutes] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [expandedPanel, setExpandedPanel] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState(null);
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({ drivers: {}, destinations: {}, routes: {}, warehouse: null });
  const subscriptionRef = useRef(null);
  const initialFitDone = useRef(false);

  // Warehouse location
  const warehouseLocation = { lat: -25.8440, lng: 28.0998, name: 'Foodline Warehouse' };

  // Re-center map function
  const recenterMap = () => {
    if (!mapInstanceRef.current) return;
    
    const map = mapInstanceRef.current;
    const bounds = [warehouseLocation];
    
    drivers.forEach(driver => {
      if (driver.location) {
        bounds.push(driver.location);
      }
      driver.orders.forEach(order => {
        if (order.address?.latitude && order.address?.longitude) {
          bounds.push({ lat: parseFloat(order.address.latitude), lng: parseFloat(order.address.longitude) });
        }
      });
    });

    if (bounds.length > 1) {
      const latLngs = bounds.map(b => [b.lat, b.lng]);
      map.fitBounds(latLngs, { padding: [50, 50], maxZoom: 14 });
    } else {
      map.setView([warehouseLocation.lat, warehouseLocation.lng], 13);
    }
  };

  // Initialize map - runs once when component mounts
  useEffect(() => {
    // Only initialize if ref exists and map doesn't
    if (!mapRef.current || mapInstanceRef.current) return;

    // Check if Leaflet is available
    if (typeof L === 'undefined') {
      setMapError('Leaflet library not loaded. Please run: npm install leaflet');
      return;
    }

    try {
      console.log('🗺️ Initializing map...');
      
      // Create map instance
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [warehouseLocation.lat, warehouseLocation.lng],
        zoom: 13,
        zoomControl: true
      });
      
      // Add tile layer
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
      }).addTo(mapInstanceRef.current);

      // Add warehouse marker immediately
      markersRef.current.warehouse = L.marker([warehouseLocation.lat, warehouseLocation.lng], { icon: warehouseIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(`<div style="text-align:center;font-weight:bold;">🏭 ${warehouseLocation.name}</div>`)
        .openPopup();

      console.log('✅ Map initialized with warehouse marker');
      setMapReady(true);

      // Force a resize after a small delay to ensure proper rendering
      setTimeout(() => {
        if (mapInstanceRef.current) {
          mapInstanceRef.current.invalidateSize();
        }
      }, 100);

    } catch (err) {
      console.error('❌ Map initialization error:', err);
      setMapError(err.message);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when drivers change
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;
    const markers = markersRef.current;

    // Clear old driver markers
    Object.values(markers.drivers).forEach(m => map.removeLayer(m));
    Object.values(markers.destinations).forEach(m => map.removeLayer(m));
    Object.values(markers.routes).forEach(m => map.removeLayer(m));
    markers.drivers = {};
    markers.destinations = {};
    markers.routes = {};

    // Add new markers
    const bounds = [warehouseLocation];

    drivers.forEach(driver => {
      if (driver.location) {
        bounds.push(driver.location);

        // Driver marker
        const driverMarker = L.marker([driver.location.lat, driver.location.lng], { 
          icon: createDriverIcon(driver.color) 
        }).addTo(map);

        const speedInfo = driver.location.speed > 0 ? `<div style="font-size:11px;color:#888;">Speed: ${Math.round(driver.location.speed * 3.6)} km/h</div>` : '';
        driverMarker.bindPopup(`
          <div style="min-width:180px;">
            <div style="font-weight:bold;color:#111;">${driver.name}</div>
            ${driver.phone ? `<div style="font-size:12px;color:#666;">📞 ${driver.phone}</div>` : ''}
            <div style="font-size:12px;color:#2563eb;margin-top:4px;">${driver.orderCount} delivery${driver.orderCount !== 1 ? 'ies' : 'y'}</div>
            ${speedInfo}
            <div style="font-size:11px;color:#aaa;margin-top:4px;">Updated: ${formatTimeAgo(driver.location.recordedAt)}</div>
          </div>
        `);

        markers.drivers[driver.id] = driverMarker;

        // Destination markers
        if (showDestinations) {
          driver.orders.forEach(order => {
            if (order.address?.latitude && order.address?.longitude) {
              const destLat = parseFloat(order.address.latitude);
              const destLng = parseFloat(order.address.longitude);
              bounds.push({ lat: destLat, lng: destLng });

              const destMarker = L.marker([destLat, destLng], { 
                icon: createDestinationIcon(driver.color) 
              }).addTo(map);

              const distance = calculateDistance(driver.location.lat, driver.location.lng, destLat, destLng);
              destMarker.bindPopup(`
                <div style="min-width:160px;">
                  <div style="font-weight:bold;color:#111;">${order.orders?.order_number || 'Order'}</div>
                  <div style="font-size:12px;color:#666;">${order.customer?.name || 'Customer'}</div>
                  <div style="font-size:11px;color:#888;margin-top:4px;">${order.address.physical_address || ''}, ${order.address.area || ''}</div>
                  <div style="font-size:11px;color:#2563eb;margin-top:4px;">${distance.toFixed(1)} km away</div>
                </div>
              `);

              markers.destinations[order.id] = destMarker;

              // Route line
              if (showRoutes) {
                const routeLine = L.polyline(
                  [[driver.location.lat, driver.location.lng], [destLat, destLng]],
                  { color: driver.color, weight: 2, opacity: 0.6, dashArray: '5, 10' }
                ).addTo(map);
                markers.routes[order.id] = routeLine;
              }
            }
          });
        }
      }
    });

    // Only fit bounds once on initial load when we first get drivers
    if (bounds.length > 1 && !initialFitDone.current) {
      const latLngs = bounds.map(b => [b.lat, b.lng]);
      map.fitBounds(latLngs, { padding: [50, 50], maxZoom: 14 });
      initialFitDone.current = true;
    }

  }, [drivers, showDestinations, showRoutes]);

  const fetchActiveDrivers = useCallback(async () => {
    try {
      // First, get ALL drivers who have recent GPS locations (active in last 30 minutes)
      const thirtyMinutesAgo = new Date(Date.now() - 30 * 60 * 1000).toISOString();
      
      const { data: locations, error: locError } = await supabase
        .from('driver_locations')
        .select('*')
        .gte('recorded_at', thirtyMinutesAgo);

      if (locError) throw locError;

      if (!locations || locations.length === 0) {
        setDrivers([]);
        setLoading(false);
        setLastUpdated(new Date());
        return;
      }

      const driverIds = locations.map(l => l.driver_id);
      
      // Get driver info
      const { data: driversData } = await supabase
        .from('drivers')
        .select('id, name, phone, user_id')
        .in('user_id', driverIds);

      const driverMap = {};
      driversData?.forEach(d => { driverMap[d.user_id] = d; });

      // Get any active assignments for these drivers
      const { data: assignments } = await supabase
        .from('driver_assignments')
        .select(`
          id, order_id, driver_id, status, assigned_at, collected_at, gatepass_issued_at,
          orders (id, order_number, total, delivery_address_id, user_id)
        `)
        .in('driver_id', driverIds)
        .in('status', ['assigned', 'collected', 'in_transit', 'delivering', 'out_for_delivery'])
        .order('assigned_at', { ascending: false });

      // Get addresses for orders
      const addressIds = (assignments || []).map(a => a.orders?.delivery_address_id).filter(Boolean);
      let addressMap = {};
      if (addressIds.length > 0) {
        const { data: addresses } = await supabase
          .from('addresses')
          .select('id, physical_address, area, latitude, longitude')
          .in('id', addressIds);
        addresses?.forEach(a => { addressMap[a.id] = a; });
      }

      // Get customer info
      const userIds = (assignments || []).map(a => a.orders?.user_id).filter(Boolean);
      let customerMap = {};
      if (userIds.length > 0) {
        const { data: customers } = await supabase
          .from('user_profiles')
          .select('id, name, phone')
          .in('id', userIds);
        customers?.forEach(c => { customerMap[c.id] = c; });
      }

      // Group assignments by driver
      const driverAssignments = {};
      (assignments || []).forEach(a => {
        if (!driverAssignments[a.driver_id]) driverAssignments[a.driver_id] = [];
        driverAssignments[a.driver_id].push({
          ...a,
          address: addressMap[a.orders?.delivery_address_id],
          customer: customerMap[a.orders?.user_id]
        });
      });

      // Build driver data - now based on GPS locations, not assignments
      const now = Date.now();
      const twoMinutesAgo = now - 2 * 60 * 1000; // 2 minutes
      
      const driversWithData = locations.map((location, index) => {
        const driverId = location.driver_id;
        const driverInfo = driverMap[driverId];
        const driverOrders = driverAssignments[driverId] || [];
        const lastUpdate = new Date(location.recorded_at).getTime();
        const isOnline = lastUpdate > twoMinutesAgo;

        return {
          id: driverId,
          name: driverInfo?.name || 'Unknown Driver',
          phone: driverInfo?.phone,
          color: DRIVER_COLORS[index % DRIVER_COLORS.length],
          location: {
            lat: parseFloat(location.latitude),
            lng: parseFloat(location.longitude),
            heading: location.heading,
            speed: location.speed,
            recordedAt: location.recorded_at
          },
          orders: driverOrders,
          orderCount: driverOrders.length,
          isOnline: isOnline,
          isMoving: isOnline && location.speed > 0
        };
      });

      setDrivers(driversWithData);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (err) {
      console.error('Error fetching drivers:', err);
      setError(err.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActiveDrivers();
    let interval;
    if (autoRefresh) interval = setInterval(fetchActiveDrivers, 15000);
    return () => { if (interval) clearInterval(interval); };
  }, [fetchActiveDrivers, autoRefresh]);

  // Recalculate isOnline status every 30 seconds based on last GPS update time
  useEffect(() => {
    const checkOnlineStatus = setInterval(() => {
      const twoMinutesAgo = Date.now() - 2 * 60 * 1000;
      
      setDrivers(prev => prev.map(driver => {
        if (!driver.location) return driver;
        
        const lastUpdate = new Date(driver.location.recordedAt).getTime();
        const isOnline = lastUpdate > twoMinutesAgo;
        
        return {
          ...driver,
          isOnline,
          isMoving: isOnline && driver.location.speed > 0
        };
      }));
    }, 30000); // Check every 30 seconds

    return () => clearInterval(checkOnlineStatus);
  }, []);

  useEffect(() => {
    const channel = supabase
      .channel('admin-driver-locations')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'driver_locations' }, (payload) => {
        if (payload.new) {
          setDrivers(prev => prev.map(driver => {
            if (driver.id === payload.new.driver_id) {
              return {
                ...driver,
                location: {
                  lat: parseFloat(payload.new.latitude),
                  lng: parseFloat(payload.new.longitude),
                  heading: payload.new.heading,
                  speed: payload.new.speed,
                  recordedAt: payload.new.recorded_at
                },
                isOnline: true, // Just received update, so they're online
                isMoving: payload.new.speed > 0
              };
            }
            return driver;
          }));
          setLastUpdated(new Date());
        }
      })
      .subscribe();

    subscriptionRef.current = channel;
    return () => { if (subscriptionRef.current) supabase.removeChannel(subscriptionRef.current); };
  }, []);

  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8">
        <div className="flex items-center justify-center gap-3 text-red-600">
          <AlertCircle size={24} />
          <span>Error: {error}</span>
          <button onClick={fetchActiveDrivers} className="ml-4 px-4 py-2 bg-red-100 rounded-lg hover:bg-red-200">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Truck size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold">Live Driver Tracking</h2>
              <p className="text-blue-100 text-sm">
                {drivers.length} active driver{drivers.length !== 1 ? 's' : ''} • {drivers.reduce((sum, d) => sum + d.orderCount, 0)} deliveries
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowDestinations(!showDestinations)} className={`p-2 rounded-lg transition-colors ${showDestinations ? 'bg-white/20' : 'bg-white/10 opacity-60'}`} title="Toggle destinations">
              <MapPin size={18} />
            </button>
            <button onClick={() => setShowRoutes(!showRoutes)} className={`p-2 rounded-lg transition-colors ${showRoutes ? 'bg-white/20' : 'bg-white/10 opacity-60'}`} title="Toggle routes">
              <Route size={18} />
            </button>
            <button onClick={recenterMap} className="p-2 bg-white/20 rounded-lg hover:bg-white/30" title="Re-center map">
              <Navigation size={18} />
            </button>
            <button onClick={() => setAutoRefresh(!autoRefresh)} className={`p-2 rounded-lg transition-colors ${autoRefresh ? 'bg-white/20' : 'bg-white/10 opacity-60'}`} title="Toggle auto-refresh">
              <RefreshCw size={18} className={autoRefresh ? 'animate-spin' : ''} style={{ animationDuration: '3s' }} />
            </button>
            <button onClick={fetchActiveDrivers} className="p-2 bg-white/20 rounded-lg hover:bg-white/30" title="Refresh data">
              <RefreshCw size={18} />
            </button>
          </div>
        </div>
        {lastUpdated && (
          <div className="flex items-center gap-1 mt-2 text-xs text-blue-100">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
            Last updated: {formatTimeAgo(lastUpdated)}
          </div>
        )}
      </div>

      <div className="flex" style={{ height: '600px' }}>
        {/* Map Container */}
        <div className="flex-1 relative">
          {/* Loading overlay */}
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-[1001]">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                <span className="text-gray-600">Loading drivers...</span>
              </div>
            </div>
          )}

          {/* Map error message */}
          {mapError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-[500]">
              <div className="text-center p-6 bg-white rounded-xl shadow-lg max-w-md">
                <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
                <h3 className="font-bold text-gray-900 mb-2">Map Failed to Load</h3>
                <p className="text-gray-600 text-sm mb-4">{mapError}</p>
                <code className="block bg-gray-100 p-2 rounded text-xs text-left">npm install leaflet</code>
              </div>
            </div>
          )}

          {/* Map div - always render it */}
          <div ref={mapRef} style={{ height: '100%', width: '100%', minHeight: '600px' }} />

          {/* No drivers overlay */}
          {drivers.length === 0 && mapReady && !loading && (
            <div className="absolute top-4 left-4 right-4 bg-white/95 backdrop-blur rounded-xl shadow-lg p-4 z-[1000]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                  <Truck size={20} className="text-gray-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-700">No active drivers</p>
                  <p className="text-sm text-gray-500">Drivers will appear here when logged in</p>
                </div>
              </div>
            </div>
          )}

          {/* GPS Status */}
          <div className="absolute bottom-4 left-4 z-[1000]">
            <div className="bg-white/95 backdrop-blur rounded-lg shadow-lg px-3 py-2 flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${drivers.some(d => d.location) ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
              <span className="text-sm font-medium text-gray-700">
                GPS {drivers.some(d => d.location) ? 'Active' : 'Waiting'}
              </span>
              {drivers.length > 0 && (
                <span className="text-xs text-gray-500">• {drivers.filter(d => d.location).length}/{drivers.length} tracking</span>
              )}
            </div>
          </div>
        </div>

        {/* Side Panel */}
        <div className={`bg-gray-50 border-l border-gray-200 transition-all ${expandedPanel ? 'w-80' : 'w-12'}`}>
          <button onClick={() => setExpandedPanel(!expandedPanel)} className="w-full p-3 flex items-center justify-center bg-gray-100 hover:bg-gray-200">
            {expandedPanel ? <ChevronDown size={20} /> : <Users size={20} />}
          </button>

          {expandedPanel && (
            <div className="overflow-y-auto" style={{ height: 'calc(100% - 48px)' }}>
              {drivers.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">No active drivers</div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {drivers.map(driver => (
                    <div
                      key={driver.id}
                      className={`p-3 hover:bg-white cursor-pointer ${selectedDriver?.id === driver.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''}`}
                      onClick={() => setSelectedDriver(selectedDriver?.id === driver.id ? null : driver)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ backgroundColor: driver.color }}>
                          {driver.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-gray-900 truncate">{driver.name}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            <span className="flex items-center gap-1"><Package size={12} />{driver.orderCount}</span>
                            {driver.location && (
                              driver.isOnline ? (
                                <span className={`flex items-center gap-1 ${driver.isMoving ? 'text-green-600' : 'text-blue-600'}`}>
                                  <Navigation size={12} className={driver.isMoving ? 'animate-pulse' : ''} />
                                  {driver.isMoving ? 'Moving' : 'Stopped'}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-gray-400">
                                  <Navigation size={12} />
                                  Offline
                                </span>
                              )
                            )}
                          </div>
                        </div>
                        {driver.location && !driver.isOnline && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded-full">Not logged in</span>
                        )}
                      </div>

                      {selectedDriver?.id === driver.id && (
                        <div className="mt-3 space-y-2">
                          {driver.phone && (
                            <a href={`tel:${driver.phone}`} className="flex items-center gap-2 text-sm text-blue-600 hover:underline" onClick={e => e.stopPropagation()}>
                              <Phone size={14} />{driver.phone}
                            </a>
                          )}
                          <div className="text-xs text-gray-500">
                            {driver.location ? `Updated: ${formatTimeAgo(driver.location.recordedAt)}` : 'Location not available'}
                          </div>
                          <div className="mt-2 space-y-2">
                            <div className="text-xs font-semibold text-gray-700 uppercase">Deliveries</div>
                            {driver.orders.map(order => (
                              <div key={order.id} className="bg-white rounded-lg p-2 text-xs border border-gray-200">
                                <div className="font-medium text-gray-900">{order.orders?.order_number}</div>
                                <div className="text-gray-600">{order.customer?.name}</div>
                                <div className="text-gray-500 truncate">{order.address?.area}</div>
                                {driver.location && order.address?.latitude && (
                                  <div className="text-blue-600 mt-1">
                                    {calculateDistance(driver.location.lat, driver.location.lng, parseFloat(order.address.latitude), parseFloat(order.address.longitude)).toFixed(1)} km
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDriverTrackingMap;
